import { animate, sequence, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, WritableSignal, signal } from '@angular/core';

import { Observable, Subject, Subscription, filter, merge, of, switchMap, take, tap } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MediaTypeEnum } from '../../../Basic/enums/media-type.enum';
import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { IframeDialogModel } from '../../../Dialogs/models/iframe-dialog.model';
import { TVEyesTimelineService } from '../../../Dialogs/services/tveyes-timeline.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { Mention } from '../../../Mentions/models/mention';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { IframeDialogComponent } from '../IframeDialog/iframe-dialog.component';
import { TVEyesIFrameWindowMessage, TVEyesIFrameWindowMessageAction } from './tveyes-iframe-window-message.model';
import { TVEyesTimelineThumbnail } from './tveyes-timeline.model';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';


enum DisableTimelineReasonEnum {
    LOADING_MENTION,
    UNSAVED_MENTION,
    EDITED_MENTION
}

interface IFrameWindowMessage {
    action: TVEyesIFrameWindowMessageAction;
    dateTime?: string;
}

@Component({
    selector: 'agpr-tveyes-mediaview-dialog',
    templateUrl: 'tveyes-mediaview-dialog.component.html',
    styleUrls: ['tveyes-mediaview-dialog.component.scss'],
    animations: [
        trigger('overlayAnimation', [
            transition('* => open', [
                style({ opacity: 0 }),
                sequence([
                    animate('0.15s', style({ opacity: 1 })),
                ])
            ]),
            transition('* => close', [
                style({ opacity: 1 }),
                sequence([
                    animate('0.15s', style({ opacity: 0 })),
                ])
            ])
        ])
    ],
    standalone: false
})
export class TVEyesMediaViewDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    static readonly TVEYES_MEDIAVIEW_DIALOG_KEY = 'TVEyesMediaViewDialog';

    currentMediaviewUrl: string;
    hasPrecisionClipPermission: boolean = false;

    @Input() mention: Mention;
    @Input() mentionSearch: MentionSearchModel;
    @Input() enablePrecisionClip: boolean = true;
    @Input() mediaviewIFrameSettings: IframeDialogModel;
    @Input() keywords: string[];

    @Output() message: EventEmitter<any> = new EventEmitter();

    @ViewChild('iframe') iframe: IframeDialogComponent;

    private readonly iframeIsLoaded$ = new Subject<void>();
    private readonly iframeAction$ = new Subject<IFrameWindowMessage>();
    private readonly updateTimelineSubject = new Subject<number>();

    private subscriptions: Subscription[] = [];

    readonly id = 'tveyesMediaViewDialog';
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly disableTimelineReason = DisableTimelineReasonEnum;
    readonly tvEyesState = TVEyesArchivedStateEnum;
    readonly mediaTypeEnum = MediaTypeEnum;

    readonly disableTimeline: WritableSignal<DisableTimelineReasonEnum> = signal(DisableTimelineReasonEnum.LOADING_MENTION);
    readonly timelineLoading = signal(false);
    readonly showTimeline = signal(false);
    readonly currentTimelinePage = signal(0);
    readonly timelineThumbnails: WritableSignal<TVEyesTimelineThumbnail[]> = signal([]);

    constructor(
        public coreDialogService: CoreDialogService,
        private tvEyesTimelineService: TVEyesTimelineService,
        private mentionService: MentionsService,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(coreDialogService, TVEyesMediaViewDialogComponent.TVEYES_MEDIAVIEW_DIALOG_KEY);

    }

    ngOnInit() {
        const iframeUrl = this.mediaviewIFrameSettings.inputs.url;
        this.currentMediaviewUrl = this.mediaviewIFrameSettings.inputs.url;
        this.tvEyesTimelineService.createTimeline(this.mention, iframeUrl, this.keywords);

        if (!this.mention.mentionId) {
            this.disableTimeline.set(DisableTimelineReasonEnum.UNSAVED_MENTION);
        } else if (this.mention.tvEyesArchiveState === TVEyesArchivedStateEnum.EDITED) {
            this.disableTimeline.set(DisableTimelineReasonEnum.EDITED_MENTION);
        }

        this.subscriptions.push(
            this.fetchThumbnails$().subscribe(thumbnails => {
                this.timelineLoading.set(false);
                this.timelineThumbnails.set(thumbnails);
            }),
            this.postActionWithRefresh$().subscribe(),
            this.currentUserStateService.getPermissions().pipe(
                filter(x => !!x)
            ).subscribe(permissions => {
                this.hasPrecisionClipPermission = permissions?.includes(userPermissionsEnum.precisionClipper);
            })
        );
    }

    toggleTimeline() {
        this.showTimeline.update(show => !show);
    }

    scrollTimeline(direction: 'back' | 'forward') {
        const page = direction === 'back'
            ? this.currentTimelinePage() - 1
            : this.currentTimelinePage() + 1;

        this.currentTimelinePage.set(page);
        this.updateTimelineSubject.next(page);
    }

    handleIframeMessage(message: MessageEvent | string) {
        if (typeof(message) === 'object' && message.origin !== 'https://mms.tveyes.com') {
            return;
        }
        if (typeof(message) === 'string') {
            // Emit for the handler in tveyes-service
            this.message.next(message);
            return;
        }
        if (JSON.parse(message.data).action === 'playerReady') {
            this.iframeIsLoaded$.next();
            // We have the iframe set to autoplay to allow the timeline to be used without
            // A "user" action in the iframe itself first. We want to pause right away.
            // See https://stackoverflow.com/questions/54948338/getting-notallowederror-play-failed-because-the-user-didnt-interact-with-the
            this.postWindowMessage('pause', null);
        }
    }

    postWindowMessage(action: TVEyesIFrameWindowMessageAction, dateTime: string) {
        this.iframeAction$.next({
            action: action,
            dateTime: dateTime
        });
    }

    openPrecisionClipDialog() {
        this.close();

        const io = {
            inputs: {
                mention: this.mention,
                mentionSearch: this.mentionSearch
            }
        };

        this.coreDialogService.open('PrecisionClipSetupDialogComponent', io);
    }

    openRevertDialog() {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Monitor.revertDialog.header',
                message: 'Monitor.revertDialog.message',
                showCancel: true,
                okLabel: 'Monitor.revertDialog.confirm'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.mentionService.revertTvEyesToOriginal(this.mention.mentionId).subscribe(
                            () => this.close()
                        );
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    close() {
        this.closeDialog();
    }

    private fetchThumbnails$(): Observable<TVEyesTimelineThumbnail[]> {
        let getInitialThumbnails$: Observable<TVEyesTimelineThumbnail[]> = this.tvEyesTimelineService.fetchInitialThumbnails();
        let getThumbnailsByPage$: (number) => Observable<TVEyesTimelineThumbnail[]>
            = this.tvEyesTimelineService.getThumbnailsByPage.bind(this.tvEyesTimelineService);
        if (this.mention.mediaType.id === MediaTypeEnum.RADIO) {
            getInitialThumbnails$ = this.tvEyesTimelineService.getPodcastThumbnailsPyPage(0);
            getThumbnailsByPage$ = this.tvEyesTimelineService.getPodcastThumbnailsPyPage.bind(this.tvEyesTimelineService);
        }

        const fetchInitialThumbnails$: Observable<TVEyesTimelineThumbnail[]> = this.iframeIsLoaded$.pipe(
            take(1),
            filter(() => !!this.mention.mentionId && !this.mention.tvEyesArchiveState), // Don't show timeline if mention is unsaved
            tap(() => this.disableTimeline.set(DisableTimelineReasonEnum.LOADING_MENTION)),
            switchMap(() => getInitialThumbnails$),
            tap(() => this.disableTimeline.set(null))
        );

        return merge(
            fetchInitialThumbnails$,
            this.updateTimelineSubject.pipe(
                tap(() => this.timelineLoading.set(true)),
                switchMap((page) => {
                    return getThumbnailsByPage$(page);
                }),
            )
        );
    }

    private postActionWithRefresh$(): Observable<void> {
        return this.iframeAction$.pipe(
            switchMap(({action, dateTime}) => this.tvEyesTimelineService.getRefreshUrl(dateTime, action).pipe(
                switchMap(refreshUrl => {
                    if (refreshUrl) {
                        this.currentMediaviewUrl = refreshUrl;
                        // Wait until refresh happens, then apply the action
                        return this.iframeIsLoaded$.pipe(
                            take(1),
                        );
                    }
                    return of(null);
                }),
                tap(() => this.iframe.postMessageToIFrame(new TVEyesIFrameWindowMessage(action, dateTime).toJSON()))
            ))
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
